import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import {
  AI_PROVIDERS,
  aiProviderLabel,
  useAccounts,
  useAiKeys,
  useCreateAiKey,
  useDeleteAiKey,
  useSites,
  useUpdateAiKey,
} from '@wordpuppi/shared';
import type { AiKeyScope, AiProviderId, AiProviderKey } from '@wordpuppi/shared';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { confirmDialog } from '@/lib/dialog';
import { toast } from 'sonner';
import { useActiveAccount } from '@/lib/activeAccount';

// AB#669: BYOK provider keys — bring your own OpenAI/Anthropic/etc key to
// unlock paid models in Baxter. Without one, Baxter is limited to the free
// OpenRouter tier the platform key is gated to.
export const Route = createFileRoute('/account/ai-keys')({
  component: AiKeysPage,
});

const SCOPE_LABELS: Record<AiKeyScope, string> = {
  user: 'Personal',
  site: 'This site',
  account: 'Account',
};

function AiKeysPage() {
  const { data: accountsData } = useAccounts();
  const accounts = accountsData?.data ?? [];

  // The account the menu switched to (AB#1014); owner-first until one is picked.
  // Picking here switches it everywhere.
  const { active, setActive } = useActiveAccount();
  const accountPid = active?.pid ?? '';
  const account = accounts.find((a) => a.pid === accountPid);
  const isOwner = account?.role === 'owner';

  const { data: sitesData } = useSites();
  const accountSites = (sitesData?.data ?? []).filter((s) => s.account_pid === accountPid);

  // Which site's shared keys are in view — also the default site for a new
  // site-scoped key. GET /keys needs a site to resolve site-scoped visibility.
  const [siteSlug, setSiteSlug] = useState('');
  const activeSiteSlug =
    siteSlug && accountSites.some((s) => s.slug === siteSlug) ? siteSlug : (accountSites[0]?.slug ?? '');

  const { data: keysData, isLoading } = useAiKeys(activeSiteSlug || undefined);
  const keys = keysData?.data ?? [];

  const createKey = useCreateAiKey();
  const updateKey = useUpdateAiKey();
  const deleteKey = useDeleteAiKey();

  const [provider, setProvider] = useState<AiProviderId>('openrouter');
  const [label, setLabel] = useState('');
  const [key, setKey] = useState('');
  const [scope, setScope] = useState<AiKeyScope>('user');
  const [formSiteSlug, setFormSiteSlug] = useState('');
  const [isShared, setIsShared] = useState(false);

  const selectAccount = (pid: string) => {
    setActive(pid);
    setSiteSlug('');
    setScope('user');
    setFormSiteSlug('');
    setIsShared(false);
  };

  const resetForm = () => {
    setLabel('');
    setKey('');
    setScope('user');
    setFormSiteSlug('');
    setIsShared(false);
  };

  const handleCreate = async () => {
    const trimmedLabel = label.trim();
    const trimmedKey = key.trim();
    if (!trimmedLabel || !trimmedKey) {
      toast.error('Enter a label and a key');
      return;
    }
    if (scope === 'site' && !formSiteSlug) {
      toast.error('Choose a site');
      return;
    }
    try {
      await createKey.mutateAsync({
        provider,
        label: trimmedLabel,
        key: trimmedKey,
        scope,
        ...(scope === 'site' ? { site_slug: formSiteSlug } : {}),
        ...(scope !== 'user' ? { is_shared: isShared } : {}),
      });
      toast.success('Key added');
      resetForm();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add key');
    }
  };

  const handleDelete = async (k: AiProviderKey) => {
    if (
      !(await confirmDialog(
        `Delete "${k.label}"? Anything using it stops working immediately. This cannot be undone.`,
        { destructive: true, confirmLabel: 'Delete key' },
      ))
    )
      return;
    try {
      await deleteKey.mutateAsync(k.pid);
      toast.success('Key deleted');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete key');
    }
  };

  const handleToggleShared = async (k: AiProviderKey, shared: boolean) => {
    try {
      await updateKey.mutateAsync({ pid: k.pid, is_shared: shared });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update key');
    }
  };

  const personalKeys = keys.filter((k) => k.scope === 'user');
  const accountScopedKeys = keys.filter((k) => k.scope === 'account');
  const siteScopedKeys = keys.filter((k) => k.scope === 'site');

  const renderRow = (k: AiProviderKey) => {
    // Non-owners only manage their own personal keys — account/site rows they
    // can see (because they're shared) stay read-only for them.
    const manageable = k.scope === 'user' || isOwner;
    return (
      <div key={k.pid} className="rounded-lg border p-3 text-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="truncate font-medium">{k.label}</span>
              <Badge variant="secondary">{aiProviderLabel(k.provider)}</Badge>
            </div>
            <div className="text-xs text-muted-foreground">
              <span className="font-mono">••••{k.key_hint}</span>
              {k.site_slug && <> · {k.site_slug}</>}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {k.scope !== 'user' && (
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                Shared with account
                <Switch
                  checked={k.is_shared}
                  disabled={!isOwner || updateKey.isPending}
                  onCheckedChange={(v) => handleToggleShared(k, v)}
                  aria-label={`Share "${k.label}" with the account`}
                />
              </label>
            )}
            {manageable && (
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => handleDelete(k)}
                disabled={deleteKey.isPending}
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
                Delete
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>AI Provider Keys</CardTitle>
          <CardDescription>
            Bring your own key to unlock paid models in Baxter. Without one, Baxter stays on
            WordPuppi's free tier.
            <span className="mt-1 block">
              A key is never shown again once saved — only its provider, label, and last 4
              characters.
            </span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {accounts.length > 1 && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">Account</span>
              <Select value={accountPid} onValueChange={selectAccount}>
                <SelectTrigger className="w-64">
                  <SelectValue>{() => account?.name ?? ''}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => (
                    <SelectItem key={a.pid} value={a.pid}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {accountSites.length > 1 && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">Viewing site</span>
              <Select value={activeSiteSlug} onValueChange={setSiteSlug}>
                <SelectTrigger className="w-64">
                  <SelectValue>
                    {() => accountSites.find((s) => s.slug === activeSiteSlug)?.name ?? ''}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {accountSites.map((s) => (
                    <SelectItem key={s.slug} value={s.slug}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : keys.length === 0 ? (
            <p className="text-sm text-muted-foreground">No keys yet.</p>
          ) : (
            <div className="space-y-4">
              {personalKeys.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Personal
                  </p>
                  {personalKeys.map(renderRow)}
                </div>
              )}
              {accountScopedKeys.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Account
                  </p>
                  {accountScopedKeys.map(renderRow)}
                </div>
              )}
              {siteScopedKeys.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Site{activeSiteSlug ? ` · ${activeSiteSlug}` : ''}
                  </p>
                  {siteScopedKeys.map(renderRow)}
                </div>
              )}
            </div>
          )}

          <div className="space-y-3 rounded-lg border p-3">
            <p className="text-sm font-medium">Add a key</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Provider</Label>
                <Select value={provider} onValueChange={(v) => setProvider(v as AiProviderId)}>
                  <SelectTrigger>
                    <SelectValue>{(v: unknown) => aiProviderLabel(String(v))}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {AI_PROVIDERS.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Label</Label>
                <Input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g. My OpenAI key"
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Key</Label>
                <Input
                  type="password"
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  placeholder="sk-…"
                  autoComplete="off"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Scope</Label>
                <Select
                  value={scope}
                  onValueChange={(v) => {
                    const next = v as AiKeyScope;
                    setScope(next);
                    if (next !== 'site') setFormSiteSlug('');
                    if (next === 'user') setIsShared(false);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue>{(v: unknown) => SCOPE_LABELS[v as AiKeyScope]}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Personal</SelectItem>
                    {isOwner && <SelectItem value="site">This site</SelectItem>}
                    {isOwner && <SelectItem value="account">Account</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              {scope === 'site' && (
                <div className="space-y-1.5">
                  <Label>Site</Label>
                  <Select value={formSiteSlug} onValueChange={setFormSiteSlug}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a site">
                        {(v: unknown) => accountSites.find((s) => s.slug === v)?.name ?? ''}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {accountSites.map((s) => (
                        <SelectItem key={s.slug} value={s.slug}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {scope !== 'user' && (
              <label className="flex items-center gap-2 text-sm">
                <Switch
                  checked={isShared}
                  onCheckedChange={setIsShared}
                  aria-label="Shared with account"
                />
                Shared with account
              </label>
            )}
            <Button onClick={handleCreate} disabled={createKey.isPending || !label.trim() || !key.trim()}>
              {createKey.isPending ? 'Adding…' : 'Add key'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
